import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import SEOHead from '@/components/feature/SEOHead';

const faqCategories = [
  {
    category: 'Getting Started',
    icon: 'ri-rocket-line',
    faqs: [
      {
        q: 'Do I need prior experience to enroll?',
        a: 'No prior experience is required. Our programs are designed for beginners as well as developing creatives. We meet each individual at their current level and guide them toward excellence.',
      },
      {
        q: 'What ages do you work with?',
        a: 'We work with a wide range of ages, including children, teens, and adults. Each program is thoughtfully tailored to fit the developmental needs of the individual.',
      },
      {
        q: 'How do I get started?',
        a: 'Getting started is simple. Enroll online, book a consultation, or call and text us directly. Our team will guide you through every step.',
      },
    ],
  },
  {
    category: 'Programs and Sessions',
    icon: 'ri-calendar-line',
    faqs: [
      {
        q: 'Do you offer in-home sessions?',
        a: 'Yes. We offer In-Home Creative Development for individuals who benefit from learning in a comfortable and familiar environment.',
      },
      {
        q: 'Do you offer night classes?',
        a: 'Yes — and this is one of our most popular options. We are open Monday through Saturday from 9am all the way to 9pm, so evening slots are available every day we operate. Night classes are ideal for students in school during the day, working adults, and busy families who cannot make it during traditional hours. You receive the exact same quality of instruction in the evening as any other time — same instructors, same equipment, same standard of excellence. There is no difference in the experience, just the time on the clock.',
      },
      {
        q: 'How do I book an evening or night class?',
        a: 'Booking an evening session is simple. You can use our AI assistant (the chat button on the site) to find an open evening slot and get it scheduled in minutes. You can also call or text us at 910-242-4567 to speak with our team directly and pick a time that works for you. If you prefer, you can also submit an enrollment request online and indicate your preferred time window — our team will match you with an available evening instructor.',
      },
      {
        q: 'Can I walk in for an evening session without booking ahead?',
        a: 'Yes. Walk-in lessons are available during all open hours, including evenings. Just show up anytime between 9am and 9pm, Monday through Saturday, and a session is $30 per hour — paid at the door. No appointment needed, no contracts. We recommend calling ahead if you want to guarantee a specific instrument or instructor for your evening slot, but walk-ins are always welcome.',
      },
      {
        q: 'Do you offer group sessions — can I bring a friend?',
        a: 'Absolutely. Group sessions are available and we love when people bring a friend, sibling, or small group. Training together creates energy, accountability, and a lot more fun. Group rates are available — just ask our team when you reach out or note it in your enrollment form. You can also indicate group enrollment directly on our online enrollment form.',
      },
      {
        q: 'How often are sessions held?',
        a: 'We offer structured membership plans that include 1 to 3 sessions per week, depending on the selected program.',
      },
      {
        q: 'What is included in the monthly membership?',
        a: 'Membership includes structured sessions, hands-on instruction, and ongoing development designed to build skill, confidence, and creativity.',
      },
    ],
  },
  {
    category: 'Equipment and Setup',
    icon: 'ri-tools-line',
    faqs: [
      {
        q: 'Do I need to have my own instrument or equipment?',
        a: 'No. For in-home sessions, we bring all necessary equipment to provide a complete learning experience. For in-house sessions, all equipment is provided on-site.',
      },
      {
        q: 'What areas do you serve for in-home sessions?',
        a: 'In-home sessions are available based on location and scheduling. Travel fees may apply depending on distance.',
      },
    ],
  },
  {
    category: 'Pricing and Payments',
    icon: 'ri-bank-card-line',
    faqs: [
      {
        q: 'Do you offer flexible payment options?',
        a: 'Yes. We offer flexible payment options, including Afterpay, to make getting started more accessible. Contact us to discuss what works best for your situation.',
      },
      {
        q: 'Do you offer discounts?',
        a: 'Yes. We offer special rates for military families, first responders, and community service professionals.',
      },
      {
        q: 'Is financial assistance available?',
        a: 'Limited financial assistance options are available. Our mission is to ensure that no creative is turned away due to financial limitations.',
      },
    ],
  },
  {
    category: 'Services',
    icon: 'ri-star-line',
    faqs: [
      {
        q: 'Do you offer services outside of lessons?',
        a: 'Yes. We provide professional creative services including live band performances, private piano experiences, photography, videography, audio production, stage support, and creative consulting.',
      },
    ],
  },

];

const faqJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: 'Do I need prior experience to enroll?', acceptedAnswer: { '@type': 'Answer', text: 'No prior experience is required. Our programs are designed for beginners as well as developing creatives. We meet each individual at their current level and guide them toward excellence.' } },
      { '@type': 'Question', name: 'What ages do you work with?', acceptedAnswer: { '@type': 'Answer', text: 'We work with a wide range of ages, including children, teens, and adults. Each program is thoughtfully tailored to fit the developmental needs of the individual.' } },
      { '@type': 'Question', name: 'Do you offer in-home sessions?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. We offer In-Home Creative Development for individuals who benefit from learning in a comfortable and familiar environment.' } },
      { '@type': 'Question', name: 'Do you offer night classes?', acceptedAnswer: { '@type': 'Answer', text: 'Yes — we are open Monday through Saturday from 9am all the way to 9pm, so evening slots are available every day we operate.' } },
      { '@type': 'Question', name: 'Do you offer flexible payment options?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. We offer flexible payment options, including Afterpay, to make getting started more accessible.' } },
      { '@type': 'Question', name: 'Do you offer discounts?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. We offer special rates for military families, first responders, and community service professionals.' } },
      { '@type': 'Question', name: 'Do I need to have my own instrument or equipment?', acceptedAnswer: { '@type': 'Answer', text: 'No. For in-home sessions, we bring all necessary equipment to provide a complete learning experience.' } },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'FAQ | Creative Institute LLC',
    url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/faq`,
    description: 'Frequently asked questions about Creative Institute LLC programs, scheduling, pricing, and getting started with mobile music and arts education in North Carolina.',
  },
];



function NightClassCallout() {
  const handleBook = () => {
    const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null;
    if (btn) btn.click();
  };

  return (
    <div id="night-classes" className="mt-6 rounded-2xl overflow-hidden border border-[#1E3A8A]/15 scroll-mt-36">
      {/* Dark header */}
      <div className="relative bg-gradient-to-r from-[#0F172A] via-[#1E3A8A] to-[#2D1B69] px-7 py-6 overflow-hidden">
        <div className="absolute top-3 right-8 w-1.5 h-1.5 bg-brand-yellow rounded-full opacity-80" />
        <div className="absolute top-8 right-20 w-1 h-1 bg-white rounded-full opacity-40" />
        <div className="absolute top-5 right-32 w-1 h-1 bg-brand-yellow rounded-full opacity-50" />
        <div className="absolute bottom-4 right-14 w-1.5 h-1.5 bg-white/30 rounded-full" />
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-yellow flex-shrink-0">
              <i className="ri-moon-fill text-brand-blue-dark text-xl" />
            </div>
            <div>
              <p className="font-body text-brand-yellow text-xs font-semibold uppercase tracking-widest mb-0.5">Evening Availability</p>
              <h4 className="font-heading text-white text-lg font-black leading-tight">Night Classes · Mon – Sat · 9am – 9pm</h4>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-yellow/20 border border-brand-yellow/40 text-brand-yellow font-body text-xs font-semibold whitespace-nowrap flex-shrink-0">
            <i className="ri-checkbox-circle-line text-sm" /> Walk-ins Welcome
          </span>
        </div>
      </div>

      {/* White body */}
      <div className="bg-white px-7 py-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-6">
          {[
            { icon: 'ri-time-line', label: 'Hours', value: '9am – 9pm, Mon–Sat' },
            { icon: 'ri-door-open-line', label: 'Walk-Ins', value: '$30/hr · No booking needed' },
            { icon: 'ri-group-line', label: 'Group Sessions', value: 'Bring a friend — ask about rates' },
          ].map((item) => (
            <div key={item.label} className="flex items-start gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#0F172A]/8 text-brand-blue-dark flex-shrink-0">
                <i className={`${item.icon} text-sm`} />
              </div>
              <div>
                <p className="font-body text-gray-400 text-xs uppercase tracking-widest mb-0.5">{item.label}</p>
                <p className="font-body text-brand-blue-dark text-sm font-semibold">{item.value}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleBook}
            className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#0F172A] to-[#1E3A8A] text-white font-body font-semibold text-xs cursor-pointer hover:opacity-85 transition-all"
          >
            <i className="ri-calendar-check-line text-sm" /> Book an Evening Session
          </button>
          <a
            href="tel:9102424567"
            className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-gray-200 text-gray-600 font-body font-semibold text-xs cursor-pointer hover:border-brand-yellow hover:text-brand-yellow-dark transition-colors"
          >
            <i className="ri-phone-line text-sm" /> Call 910-242-4567
          </a>
          <Link
            to="/enroll"
            className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-gray-200 text-gray-600 font-body font-semibold text-xs cursor-pointer hover:border-brand-yellow hover:text-brand-yellow-dark transition-colors"
          >
            <i className="ri-user-add-line text-sm" /> Enroll Online
          </Link>
        </div>
      </div>
    </div>
  );
}

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`border rounded-xl overflow-hidden transition-all duration-300 ${
        open ? 'border-brand-purple/30 bg-[#F5F3FF]/50' : 'border-gray-100 bg-white hover:border-brand-purple/20 hover:translate-y-[-2px]'
      }`}
      style={{ transition: 'all 0.3s ease' }}
    >
      <button
        className="w-full flex items-center justify-between gap-4 px-6 py-5 cursor-pointer text-left"
        onClick={() => setOpen(!open)}
      >
        <span className="font-body font-semibold text-brand-blue-dark text-sm leading-snug">{q}</span>
        <div
          className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${
            open ? 'bg-brand-purple text-white rotate-45' : 'bg-brand-purple/10 text-brand-purple'
          }`}
        >
          <i className="ri-add-line text-base" />
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-500 ${open ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <p className="font-body text-gray-500 text-sm leading-relaxed px-6 pb-5">{a}</p>
      </div>
    </div>
  );
}

function PageHero() {
  return (
    <section className="relative min-h-[56vh] flex items-end overflow-hidden">
      <div className="absolute inset-0 z-0 bg-gradient-to-br from-[#1E3A8A] via-[#2D1B69] to-[#5B21B6]" />
      <div className="absolute inset-0 z-0 flex items-center justify-center">
        <img
          src="https://static.readdy.ai/image/b0ab6c1eb002a8cf89683099ba8ee0be/a9a19e6966780984f72576a4f2fc1316.png"
          alt=""
          className="w-[480px] h-auto object-contain opacity-[0.06] mix-blend-screen pointer-events-none select-none"
        />
      </div>
      <div className="absolute inset-0 z-0 bg-gradient-to-t from-[#1E3A8A]/70 via-transparent to-transparent" />
      <div className="absolute top-24 right-20 w-72 h-72 bg-brand-yellow/8 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-12 w-56 h-56 bg-brand-purple-light/10 rounded-full blur-2xl pointer-events-none" />
      <div className="relative z-10 w-full max-w-screen-xl mx-auto px-8 pb-20 pt-40">
        <span className="inline-block px-4 py-1.5 rounded-full border border-brand-yellow/50 text-brand-yellow font-body text-xs uppercase tracking-widest bg-brand-yellow/10 mb-6">
          Frequently Asked Questions
        </span>
        <h1 className="font-heading text-white text-6xl lg:text-7xl font-black mb-5 leading-tight max-w-3xl">
          Got Questions?<br />
          <span className="text-brand-yellow">We Have Answers.</span>
        </h1>
        <p className="font-body text-white/70 text-xl max-w-2xl leading-relaxed">
          Everything you need to know about programs, scheduling, pricing, and getting started at Creative Institute.
        </p>
      </div>
    </section>
  );
}

function QuickLinksBar() {
  return (
    <section className="bg-white border-b border-gray-100 py-5 sticky top-[72px] z-40">
      <div className="max-w-screen-xl mx-auto px-8">
        <div className="flex items-center gap-3 overflow-x-auto pb-1 scrollbar-hide">
          <span className="font-body text-xs text-gray-400 uppercase tracking-wider whitespace-nowrap flex-shrink-0 mr-2">
            Jump to:
          </span>

          {/* Night Classes highlight shortcut */}
          <a
            href="#night-classes"
            className="whitespace-nowrap inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-brand-yellow/60 bg-brand-yellow/10 font-body text-xs font-semibold text-brand-blue-dark hover:bg-brand-yellow/20 hover:border-brand-yellow transition-all cursor-pointer flex-shrink-0"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-moon-fill text-[11px] text-amber-500" />
            </span>
            Night Classes
          </a>

          <span className="w-px h-4 bg-gray-200 flex-shrink-0" />

          {faqCategories.map((cat) => (
            <a
              key={cat.category}
              href={`#${cat.category.toLowerCase().replace(/\s+/g, '-')}`}
              className="whitespace-nowrap px-4 py-2 rounded-full border border-gray-200 font-body text-xs text-brand-blue-dark hover:border-brand-purple hover:text-brand-purple hover:bg-[#F5F3FF] transition-all cursor-pointer flex-shrink-0"
            >
              {cat.category}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQContent() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.05 });
  return (
    <section ref={ref} className="py-24 bg-gradient-to-br from-[#F8FAFF] via-white to-[#F5F3FF]">
      <div className="max-w-screen-lg mx-auto px-8">
        <div className="space-y-16">
          {faqCategories.map((cat, catIdx) => (
            <div
              key={cat.category}
              id={cat.category.toLowerCase().replace(/\s+/g, '-')}
              className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''}`}
              style={{ transitionDelay: `${catIdx * 80}ms` }}
            >
              <div className="flex items-center gap-3 mb-7">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#1E3A8A] to-[#5B21B6] text-brand-yellow flex-shrink-0">
                  <i className={`${cat.icon} text-lg`} />
                </div>
                <h2 className="font-heading text-2xl font-black text-brand-blue-dark">{cat.category}</h2>
                <div className="flex-1 h-px bg-gradient-to-r from-brand-purple/20 to-transparent" />
              </div>
              <div className="space-y-3 pl-0">
                {cat.faqs.map((faq) => (
                  <FAQItem key={faq.q} q={faq.q} a={faq.a} />
                ))}
              </div>

              {cat.category === 'Programs and Sessions' && <NightClassCallout />}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactCTA() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });
  return (
    <section ref={ref} className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A8A] via-[#2D1B69] to-[#5B21B6]" />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <img
          src="https://static.readdy.ai/image/b0ab6c1eb002a8cf89683099ba8ee0be/a9a19e6966780984f72576a4f2fc1316.png"
          alt=""
          className="w-[360px] h-auto object-contain opacity-[0.05] mix-blend-screen select-none"
        />
      </div>
      <div className="relative z-10 max-w-screen-md mx-auto px-8 text-center">
        <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''}`}>
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-brand-yellow mx-auto mb-6">
            <i className="ri-message-3-line text-3xl text-brand-blue-dark" />
          </div>
          <h2 className="font-heading text-4xl lg:text-5xl font-black text-white mb-4">
            Still Have Questions?
          </h2>
          <p className="font-body text-white/70 text-base leading-relaxed mb-8 max-w-md mx-auto">
            Our team is here to help. Reach out directly and we will walk you through everything you need to know.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="whitespace-nowrap px-8 py-4 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm cursor-pointer hover:bg-brand-yellow-light transition-all"
            >
              Contact Us
            </Link>
            <a
              href="tel:9102424567"
              className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full border-2 border-white/40 text-white font-body font-semibold text-sm cursor-pointer hover:border-brand-yellow hover:text-brand-yellow transition-all"
            >
              <i className="ri-phone-line" /> Call for Pricing
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function FAQPage() {
  return (
    <main>
      <SEOHead
        title="FAQ | Creative Institute LLC — Mobile Music & Arts Education NC"
        description="Frequently asked questions about Creative Institute LLC programs, scheduling, pricing, and getting started with mobile music and arts education across North Carolina."
        keywords="Creative Institute FAQ, music lessons questions, mobile arts education NC, night classes music, in-home music lessons FAQ"
        canonical="/faq"
        jsonLd={faqJsonLd}
      />
      <PageHero />
      <QuickLinksBar />
      <FAQContent />
      <ContactCTA />
    </main>
  );
}
